/*
 * Description:
 * ---------------------
 * WAN display names — the single place the console turns "enp3s0" into
 * "Starlink", and paints it the colour of the cable it is plugged in with.
 *
 * THE RULE
 *   Every panel renders wanName(ifname). Nothing renders a raw ifname except
 *   where the ifname itself is the fact being reported (the WAN detail card's
 *   hardware row). Every REQUEST still sends the real ifname: a label is a
 *   presentation concern and never crosses into a policy rule, a stress target,
 *   an AQM apply, or a chart series key. Charts especially — the series key is
 *   the ifname, the legend text is the label.
 *
 *   Where a link stands alone as an element's whole text (a card title, a
 *   table row, a checkbox) it renders as <WanLabel>, which is that name as a
 *   tag in its cable's colour. Inside a sentence, a tooltip or a chart it
 *   stays wanName(), and so does a name beside a swatch that ties it to a
 *   chart's series (Bonding, Interface Traffic, Pilot Insights): two colours
 *   on one link would contradict each other.
 *
 * WHERE THE NAME LIVES
 *   On the appliance, at /wans/labels, not in this browser, and the cable
 *   colour beside it. Both describe the box's wiring, so every operator and the
 *   on-device console see the same ones. See cloud-anchor/pkg/api/wanlabels.go.
 *
 * WHY A CONTEXT AND NOT A PROP
 *   Around twenty panels render a WAN name, most of them fetching their own
 *   per-WAN data from a different route (/health, /stats/wan, /aqm/status,
 *   telemetry). Threading a map through all of them would guarantee that the
 *   next panel forgets. One provider, one hook, and a panel gets it right by
 *   default.
 *
 * ! file components/WanNames.js
 * ---------------
 * \author: Tolga O. Atalay
 * \Affiliation: A2 Labs LLC / A12XT Inc.
 * \date: 2025
 */

import React, {
  createContext, useCallback, useContext, useEffect, useMemo, useRef, useState,
} from "react";
import { FiEdit2, FiCheck, FiX, FiDroplet } from "react-icons/fi";
import { nodeApiUrlFor } from "../serverApi";
import { fetchWithTimeout } from "../utils/fetchWithTimeout";

/* Matches maxWANLabelLen in pkg/api/wanlabels.go. The edge truncates anyway;
   this stops the field from accepting what it would silently lose. */
export const MAX_WAN_LABEL = 32;

/* The colours patch cables come in. The edge stores only the key, so what each
   looks like is decided here and nowhere else; ink is the text drawn on it. */
export const CABLE_COLORS = [
  { key: "blue", label: "Blue", paint: "#2563eb", ink: "#ffffff" },
  { key: "grey", label: "Grey", paint: "#9ca3af", ink: "#0f172a" },
  { key: "yellow", label: "Yellow", paint: "#facc15", ink: "#0f172a" },
  { key: "green", label: "Green", paint: "#16a34a", ink: "#ffffff" },
  { key: "red", label: "Red", paint: "#dc2626", ink: "#ffffff" },
  { key: "orange", label: "Orange", paint: "#f97316", ink: "#0f172a" },
  { key: "purple", label: "Purple", paint: "#7c3aed", ink: "#ffffff" },
  { key: "pink", label: "Pink", paint: "#f472b6", ink: "#0f172a" },
  { key: "white", label: "White", paint: "#ffffff", ink: "#0f172a" },
  { key: "black", label: "Black", paint: "#111827", ink: "#ffffff" },
];
const CABLE = new Map(CABLE_COLORS.map((c) => [c.key, c]));

const WanNamesContext = createContext(null);

/* Outside the provider — the fleet views, an error boundary — a WAN still has
   to render. Falling back to identity keeps every consumer unconditional. */
const IDENTITY = {
  wanName: (iface) => iface || "",
  isNamed: () => false,
  wanColor: () => null,
  labels: {},
  rename: async () => {},
  recolor: async () => {},
  saving: "",
  error: "",
  supported: false,
  colorable: false,
};

export function useWanNames() {
  return useContext(WanNamesContext) || IDENTITY;
}

/* The common case: one name, one call site. */
export function useWanName() {
  return useWanNames().wanName;
}

/* The edge's answer in one shape. An edge older than cable colours answers
   without "colors", and must not be offered a picker that can only fail. */
function readStore(body) {
  const obj = (v) => (v && typeof v === "object" ? v : null);
  return {
    labels: obj(body?.labels) || {},
    colors: obj(body?.colors) || {},
    colorable: !!obj(body?.colors),
  };
}
const EMPTY = readStore(null);

export function WanNamesProvider({ nodeId, children }) {
  const [store, setStore] = useState(EMPTY);
  const [saving, setSaving] = useState("");
  const [error, setError] = useState("");
  /* An edge on an older build has no /wans/labels. It must not be offered a
     rename control that can only fail — it shows plain ifnames instead. */
  const [supported, setSupported] = useState(false);

  /* Labels change only when an operator renames something, so this is a load,
     not a poll. A rename updates state from the response the edge returns.

     A LABEL MAP BELONGS TO ONE EDGE. The provider is mounted once for the whole
     console and only its nodeId prop changes, so the map has to be dropped here
     rather than by a remount. Holding the previous edge's map while the new
     one's is in flight puts the last edge's names on this edge's links, and
     because this is a load and not a poll, an edge whose fetch fails or times
     out keeps wearing them for as long as it stays selected. That reads as a
     rename that crossed between appliances; nothing crossed, the console was
     showing one box's names on another. */
  useEffect(() => {
    setStore(EMPTY);
    setSupported(false);
    setError("");
    if (!nodeId) return;
    let active = true;
    (async () => {
      const res = await fetchWithTimeout(nodeApiUrlFor(nodeId)("/wans/labels")).catch(() => null);
      if (!active || !res || !res.ok) return;
      const body = await res.json().catch(() => null);
      if (!active) return;
      setSupported(true);
      setStore(readStore(body));
    })();
    return () => { active = false; };
  }, [nodeId]);

  /* One field per request. The edge leaves a field that is not sent as it
     is, so a rename cannot clear a colour, nor a colour a name. */
  const put = useCallback(async (iface, change) => {
    if (!nodeId || !iface) return;
    setSaving(iface);
    setError("");
    try {
      const res = await fetchWithTimeout(nodeApiUrlFor(nodeId)("/wans/labels"), {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ iface, ...change }),
      });
      if (!res.ok) {
        setError((await res.text().catch(() => "")) || `HTTP ${res.status}`);
        return;
      }
      setStore(readStore(await res.json()));
    } finally {
      setSaving("");
    }
  }, [nodeId]);

  const rename = useCallback((iface, label) => put(iface, { label }), [put]);
  const recolor = useCallback((iface, color) => put(iface, { color }), [put]);

  const value = useMemo(() => ({
    labels: store.labels,
    saving,
    error,
    supported,
    colorable: store.colorable,
    rename,
    recolor,
    /* The one function the rest of the console calls. Unknown or unnamed
       interfaces return their own name, so a panel never has to branch. */
    wanName: (iface) => (iface ? store.labels[iface] || iface : ""),
    isNamed: (iface) => !!(iface && store.labels[iface]),
    /* The cable colour a link was given, or null. A key this console has no
       paint for, chosen on a newer one, reads as none. */
    wanColor: (iface) => (iface && CABLE.get(store.colors[iface])) || null,
  }), [store, saving, error, supported, rename, recolor]);

  return <WanNamesContext.Provider value={value}>{children}</WanNamesContext.Provider>;
}

/* A link standing alone as an element's whole text. With a cable colour it is
   a tag in that colour; with none it is the bare name, so a box nobody
   coloured renders exactly as it did. */
export function WanLabel({ iface }) {
  const { wanName, wanColor } = useWanNames();
  const cable = wanColor(iface);
  if (!cable) return wanName(iface);
  return (
    <span className="wan-tag" style={{ "--wan-paint": cable.paint, "--wan-ink": cable.ink }}>
      {wanName(iface)}
    </span>
  );
}

/* ── Inline rename and colour controls ───────────────────────────────────────
   Live on the WAN cards in the Diagnostics → WAN view, which is the one screen
   that presents a link as an object rather than as a row in someone else's
   table. Click the pencil, type, Enter; clearing the field restores the
   ifname. Click the drop and pick the colour of the link's cable. */
export function WanNameEditor({ iface, className = "" }) {
  const { wanName, isNamed, wanColor, rename, recolor, saving, supported, colorable } = useWanNames();
  const [editing, setEditing] = useState(false);
  const [picking, setPicking] = useState(false);
  const [draft, setDraft] = useState("");
  const inputRef = useRef(null);

  const busy = saving === iface;
  const named = isNamed(iface);
  const cable = wanColor(iface);

  useEffect(() => {
    if (editing && inputRef.current) inputRef.current.select();
  }, [editing]);

  const open = () => {
    setDraft(named ? wanName(iface) : "");
    setEditing(true);
  };

  const commit = async () => {
    setEditing(false);
    if (draft.trim() === (named ? wanName(iface) : "")) return; // nothing changed
    await rename(iface, draft);
  };

  const choose = async (key) => {
    setPicking(false);
    if (key === (cable?.key || "")) return;
    await recolor(iface, key);
  };

  if (!supported) {
    return <span className={`wan-rename ${className}`}><span className="wan-rename-name">{wanName(iface)}</span></span>;
  }

  if (editing) {
    return (
      <span className={`wan-rename ${className}`}>
        <input
          ref={inputRef}
          className="wan-rename-input"
          defaultValue={draft}
          maxLength={MAX_WAN_LABEL}
          placeholder={iface}
          autoFocus
          onChange={(e) => setDraft(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === "Enter") commit();
            if (e.key === "Escape") setEditing(false);
          }}
          onBlur={commit}
        />
        <button type="button" className="wan-rename-btn" title="Save"
                onMouseDown={(e) => e.preventDefault()} onClick={commit}>
          <FiCheck size={13} />
        </button>
        <button type="button" className="wan-rename-btn" title="Cancel"
                onMouseDown={(e) => e.preventDefault()} onClick={() => setEditing(false)}>
          <FiX size={13} />
        </button>
      </span>
    );
  }

  if (picking) {
    return (
      <span className={`wan-rename ${className}`} role="group" aria-label="Cable colour"
            onKeyDown={(e) => { if (e.key === "Escape") setPicking(false); }}>
        {CABLE_COLORS.map((c, i) => (
          <button key={c.key} type="button" className="wan-swatch" title={c.label} aria-label={c.label}
                  aria-pressed={cable?.key === c.key} autoFocus={cable ? cable.key === c.key : i === 0}
                  style={{ "--wan-paint": c.paint }} onClick={() => choose(c.key)} />
        ))}
        <button type="button" className="wan-swatch wan-swatch-none" title="No colour" aria-label="No colour"
                aria-pressed={!cable} onClick={() => choose("")} />
        <button type="button" className="wan-rename-btn" title="Cancel" onClick={() => setPicking(false)}>
          <FiX size={13} />
        </button>
      </span>
    );
  }

  return (
    <span className={`wan-rename ${className}`}>
      <span className="wan-rename-name"><WanLabel iface={iface} /></span>
      <button
        type="button"
        className="wan-rename-btn"
        disabled={busy}
        /* Says what the name is FOR, and that it is only a name — the first
           question anyone asks before renaming a network interface. */
        title={named
          ? `Rename this link (currently ${iface}). Display name only — the interface itself is untouched.`
          : "Give this link a name. Display name only — the interface itself is untouched."}
        onClick={open}
      >
        <FiEdit2 size={12} />
      </button>
      {colorable && (
        <button
          type="button"
          className="wan-rename-btn"
          disabled={busy}
          aria-label="Cable colour"
          title="Colour this link like its cable, so the screen matches the box. Display only."
          onClick={() => setPicking(true)}
        >
          <FiDroplet size={12} />
        </button>
      )}
    </span>
  );
}

export default WanNamesProvider;
